import useSWR from "swr"

import { LCTOnlyDatasets } from "../../../constants/enums"
import { useGetUserData } from "../../../hooks/useGetUserData"
import { canAccessProtectedRoutes } from "../../../utils/auth/authUtils"
import { getClientProtected } from "../clients/clients"
import {
    IV2gSearchResults,
    mapEvacToV2g,
} from "../mappers/searchV2gDevicesMapper"
import { SEARCH_CONFIG } from "../swrConfig"
import { SEARCH_DEMAND_DEVICES } from "../urls/hybrid"

type TUseSearchV2gDevices = {
    data: IV2gSearchResults | undefined
    isLoading: boolean
    isError: boolean
    isValidating: boolean
}

// API caps `limit` at 100
const SEARCH_LIMIT = "100"

/**
 * Hook for fetching V2G bundle devices from the LCT (EVAC) search-protected
 * endpoint. Inverter (TTR) side is intentionally not fetched yet — re-add
 * when the inverter half of the V2G assessor flow is ready.
 *
 * LCT filter: EVAC devices where V2G_TTRStatus is populated (applied in mapper).
 */
const useSearchV2gDevices = (): TUseSearchV2gDevices => {
    const { isSignedIn, groups } = useGetUserData()
    const canAccessProtected = canAccessProtectedRoutes(isSignedIn, groups)

    // Fetch EVAC devices (V2G EVCPs)
    // Backend filter: device_type=V2G -> V2G_Capability ILIKE 'Yes' (API-Hybrid PR 19721)
    // Mapper then narrows to rows with V2G_TTRStatus populated (linked to a TTR)
    const evacParams = new URLSearchParams()
    evacParams.set("limit", SEARCH_LIMIT)
    evacParams.set("device_type", "V2G")
    evacParams.set("compliance_status", "Awaiting assessment")
    const evacUrl = canAccessProtected
        ? SEARCH_DEMAND_DEVICES(
              evacParams.toString(),
              LCTOnlyDatasets.EVAC,
              true
          )
        : null

    const {
        data: evacData,
        error: evacError,
        isLoading: evacLoading,
        isValidating: evacValidating,
    } = useSWR(
        evacUrl,
        async (url: string): Promise<IV2gSearchResults> => {
            const res = await getClientProtected(url)
            return mapEvacToV2g(res)
        },
        SEARCH_CONFIG
    )

    return {
        data: evacData,
        isLoading: evacLoading,
        isError: !!evacError,
        isValidating: evacValidating,
    }
}

export default useSearchV2gDevices
